import { AxiosError } from "axios";
import jwtDecode from "jwt-decode";
import { QueryFunctionContext } from "react-query";
import { queryClient } from "../queries/client";
import {
  useAuthStore,
  useAuthorizationStore,
  useCompanyStore,
  usePermissionStore,
} from "../store";
import idServerApi from "./idServerApi";

export type EquionSession = {
  type: "EQUION";
  userId: string;
  sid: string;
  showEmployeeSwitchNudge?: boolean;
};

const typedHeaders = { "X-Hissa-Auth-Request": "1" };
let currentEquionSession: EquionSession | null = null;
let sessionGeneration = 0;
let redirecting = false;

function errorCode(error: unknown) {
  return (
    (error as AxiosError<{ code?: string }>)?.response?.data?.code ||
    (error as { code?: string } | null)?.code
  );
}

function accountChanged() {
  return Object.assign(new Error("The active Admin account changed"), {
    code: "ACCOUNT_CHANGED",
  });
}

function sameSession(left: EquionSession, right: EquionSession) {
  return left.userId === right.userId && left.sid === right.sid;
}

function parseSession(value: any): EquionSession {
  if (
    value?.type !== "EQUION" ||
    typeof value.userId !== "string" ||
    !/^\d+$/.test(value.userId) ||
    typeof value.sid !== "string" ||
    !value.sid
  ) {
    throw accountChanged();
  }
  return { type: "EQUION", userId: value.userId, sid: value.sid };
}

export function tokenMatchesEquionSession(
  token: string,
  session: EquionSession
) {
  try {
    const claims = jwtDecode<{ type?: string; id?: string | number }>(
      token.replace(/^Bearer\s+/i, "")
    );
    return claims.type === "EQUION" && String(claims.id) === session.userId;
  } catch {
    return false;
  }
}

async function retryTransition<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (
      !["RETRY_REFRESH", "SESSION_TRANSITION", "SESSION_CONFLICT"].includes(
        errorCode(error) || ""
      )
    ) {
      throw error;
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, 250);
    });
    return request();
  }
}

export function getEquionSession() {
  return currentEquionSession;
}

export async function hasEmployeePortalAccess(
  expectedSession: EquionSession
): Promise<boolean> {
  const generation = sessionGeneration;
  if (
    !currentEquionSession ||
    !sameSession(currentEquionSession, expectedSession)
  ) {
    throw accountChanged();
  }
  const response = await idServerApi.post(
    "auth/employee-portal-access",
    { expectedSession: parseSession(expectedSession) },
    { headers: typedHeaders }
  );
  if (
    generation !== sessionGeneration ||
    !currentEquionSession ||
    !sameSession(currentEquionSession, expectedSession)
  ) {
    throw accountChanged();
  }
  return response.data?.data?.access === true;
}

export function isEquionSessionInvalid(error: unknown) {
  return (
    (error as AxiosError)?.response?.status === 401 ||
    errorCode(error) === "ACCOUNT_CHANGED" ||
    errorCode(error) === "SESSION_INVALID"
  );
}

export function isRetryableIdentityError(error: unknown) {
  if (isEquionSessionInvalid(error)) return false;
  const axiosError = error as AxiosError;
  if (axiosError?.code === "ERR_CANCELED") return false;
  if (
    ["RETRY_REFRESH", "SESSION_TRANSITION", "SESSION_CONFLICT"].includes(
      errorCode(error) || ""
    )
  ) {
    return true;
  }
  const status = axiosError?.response?.status;
  return (
    status === undefined || status === 408 || status === 429 || status >= 500
  );
}

export function clearEquionClientState() {
  sessionGeneration += 1;
  currentEquionSession = null;
  for (const key of [
    "accesstoken",
    "currentUser",
    "companyId",
    "hissaUrl",
    "x-usr-token",
  ]) {
    sessionStorage.removeItem(key);
  }
  queryClient.clear();
  useAuthorizationStore.getState().clearAuthorization();
  usePermissionStore.getState().clearPermission();
  useCompanyStore.getState().clearCompanyData();
  useAuthStore.getState().clear();
}

function redirectToSignIn(url: string) {
  if (redirecting) return;
  redirecting = true;
  clearEquionClientState();
  window.location.assign(url);
}

export function redirectToAdminSignIn() {
  redirectToSignIn(generateRedirectUrl());
}

export function redirectAfterAdminSignOut() {
  redirectToSignIn(generateSignOutRedirectUrl());
}

export async function checkHeartBeat(): Promise<EquionSession> {
  const generation = sessionGeneration;
  const response = await retryTransition(() =>
    idServerApi.get("auth/heartbeat", {
      params: { type: "EQUION" },
      headers: typedHeaders,
    })
  );
  const session = parseSession(response.data.data);
  if (
    generation !== sessionGeneration ||
    (currentEquionSession && !sameSession(currentEquionSession, session))
  ) {
    throw accountChanged();
  }
  const token = useAuthStore.getState().accessToken;
  if (token && !tokenMatchesEquionSession(token, session)) {
    throw accountChanged();
  }
  currentEquionSession = session;
  return response.data.data?.showEmployeeSwitchNudge === true
    ? { ...session, showEmployeeSwitchNudge: true }
    : session;
}

export async function refetchAccessToken(
  expectedSession?: EquionSession
): Promise<string> {
  const generation = sessionGeneration;
  if (
    expectedSession &&
    (!currentEquionSession ||
      !sameSession(expectedSession, currentEquionSession))
  ) {
    throw accountChanged();
  }
  const session =
    expectedSession || currentEquionSession || (await checkHeartBeat());
  const response = await retryTransition(() =>
    idServerApi.post(
      "auth/equion/token",
      { expectedSession: session },
      { headers: typedHeaders }
    )
  );
  if (
    generation !== sessionGeneration ||
    !currentEquionSession ||
    !sameSession(currentEquionSession, session)
  ) {
    throw accountChanged();
  }
  const grant = response.data.data;
  if (
    typeof grant?.accessToken !== "string" ||
    !grant.accessToken ||
    /^Bearer\s/i.test(grant.accessToken) ||
    !sameSession(session, parseSession(grant))
  ) {
    throw accountChanged();
  }
  const token = `Bearer ${grant.accessToken}`;
  if (!tokenMatchesEquionSession(token, session)) {
    throw accountChanged();
  }
  return token;
}

export async function signOut(
  _data: unknown
): Promise<{ message: string; data: unknown }> {
  let session = currentEquionSession;
  if (!session) {
    try {
      session = await checkHeartBeat();
    } catch (error) {
      if (!isEquionSessionInvalid(error)) throw error;
      return { message: "Logged out successfully", data: null };
    }
  }
  try {
    const response = await idServerApi.post(
      "auth/logout",
      { sid: session.sid },
      { params: { type: "EQUION" }, headers: typedHeaders }
    );
    return response.data;
  } catch (error) {
    if (!isEquionSessionInvalid(error)) throw error;
    return { message: "Logged out successfully", data: null };
  }
}

export async function getPlatformInfo(
  context: QueryFunctionContext
): Promise<any> {
  const accesstoken = context.queryKey[1] as string;
  return idServerApi
    .get(`auth/user/platform`, {
      headers: { accesstoken },
    })
    .then((res: any) => res.data.data);
}

function identityClientBaseUrl() {
  return (
    process.env.REACT_APP_ID_SERVER_CLIENT_URL ??
    "https://identity.dev.hissa.com"
  ).replace(/\/+$/, "");
}

export function getIdentityDashboardUrl() {
  return `${identityClientBaseUrl()}/landing/dashboard`;
}

export function generateRedirectUrl(redirectUrl = window.location.href) {
  const url = new URL(`${identityClientBaseUrl()}/login`);
  url.searchParams.set("account", "EQUION");
  url.searchParams.set("platform", "esop");
  url.searchParams.set("redirect", redirectUrl);
  return url.toString();
}

export function generateSignOutRedirectUrl() {
  return generateRedirectUrl(`${window.location.origin}/`);
}
